// libs
import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router';

// components
import Coachmark from 'shared/components/Coachmark';

// styles
import { CoachmarkWrapper } from './styles';

const COACHMARK_COUNT_KEY = 'store_toggle_coachmark_count';
export const COACHMARK_SHOWN_KEY = 'store_toggle_coachmark_shown';
const HP_COACHMARK_COUNT_KEY = 'hp_store_coachmark_count';
const HP_COACHMARK_TIMESTAMP_KEY = 'hp_store_coachmark_timestamp';
const HP_COACHMARK_PERIOD_COUNT_KEY = 'hp_store_coachmark_period_count';
const STORE_SWITCHED_VIA_TOGGLE_KEY = 'store_switched_via_toggle';

interface HomepageCoachmarkConfig {
  enabled: boolean;
  title: string;
  description: string;
  cta: string;
  iconUrl: string;
  maxOccurrence: number;
  cooldownPeriodMs: number;
  maxPerPeriod: number;
  autoHideDuration?: number;
}

interface CoachmarkConfig {
  enabled: boolean;
  id: string;
  title: string;
  description: string;
  cta: string;
  iconUrl: string;
  maxOccurrence: number;
  autoHideDuration?: number;
  homepageCoachmark?: HomepageCoachmarkConfig;
}

interface StoreToggleCoachmarkProps {
  currentStoreId: string;
  targetStoreId: string;
  coachmarkConfig: CoachmarkConfig;
}

function StoreToggleCoachmark({
  currentStoreId,
  targetStoreId,
  coachmarkConfig,
}: StoreToggleCoachmarkProps) {
  const [showCoachmark, setShowCoachmark] = useState(false);
  const [isHomepageCoachmark, setIsHomepageCoachmark] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    // Check if we should show the coachmark
    // Only show if store was switched via toggle (flag exists in sessionStorage)
    const storeSwitchedViaToggle = sessionStorage.getItem(STORE_SWITCHED_VIA_TOGGLE_KEY);

    // Get current count from localStorage
    const currentCount = parseInt(localStorage.getItem(COACHMARK_COUNT_KEY) || '0', 10);

    if (
      coachmarkConfig.enabled &&
      currentStoreId === targetStoreId &&
      storeSwitchedViaToggle === targetStoreId &&
      currentCount < coachmarkConfig.maxOccurrence
    ) {
      setShowCoachmark(true);
      // Increment count
      localStorage.setItem(COACHMARK_COUNT_KEY, String(currentCount + 1));
      // Signal that coachmark is being shown (so toast can suppress itself)
      sessionStorage.setItem(COACHMARK_SHOWN_KEY, 'true');
      // Clear the flag so coachmark doesn't show again on refresh
      sessionStorage.removeItem(STORE_SWITCHED_VIA_TOGGLE_KEY);
      return;
    }

    // Homepage coachmark: show on fashion homepage to nudge users to switch to Luxe
    const hpConfig = coachmarkConfig.homepageCoachmark;
    if (
      hpConfig?.enabled &&
      currentStoreId !== targetStoreId &&
      pathname === '/'
    ) {
      const hpCount = parseInt(localStorage.getItem(HP_COACHMARK_COUNT_KEY) || '0', 10);
      if (hpCount >= hpConfig.maxOccurrence) {
        return;
      }

      const lastShownTimestamp = Number(localStorage.getItem(HP_COACHMARK_TIMESTAMP_KEY) || 0);
      const now = Date.now();
      const periodElapsed = now - lastShownTimestamp >= hpConfig.cooldownPeriodMs;

      // Reset period count when cooldown period has elapsed
      if (periodElapsed) {
        localStorage.setItem(HP_COACHMARK_PERIOD_COUNT_KEY, '0');
        localStorage.setItem(HP_COACHMARK_TIMESTAMP_KEY, String(now));
      }

      const periodCount = parseInt(localStorage.getItem(HP_COACHMARK_PERIOD_COUNT_KEY) || '0', 10);
      const maxPerPeriod = hpConfig.maxPerPeriod || 1;

      if (periodCount >= maxPerPeriod) {
        return;
      }

      localStorage.setItem(HP_COACHMARK_COUNT_KEY, String(hpCount + 1));
      localStorage.setItem(HP_COACHMARK_PERIOD_COUNT_KEY, String(periodCount + 1));
      setIsHomepageCoachmark(true);
      setShowCoachmark(true);
    }
  }, [currentStoreId, targetStoreId, coachmarkConfig, pathname]);

  const activeConfig = isHomepageCoachmark
    ? coachmarkConfig.homepageCoachmark!
    : coachmarkConfig;

  useEffect(() => {
    const duration = activeConfig.autoHideDuration;
    if (!showCoachmark || !duration) return;

    const timer = setTimeout(() => {
      setShowCoachmark(false);
    }, duration);

    return () => clearTimeout(timer);
  }, [showCoachmark, activeConfig.autoHideDuration]);

  const handleCoachmarkDismiss = () => {
    setShowCoachmark(false);
  };

  const handleCoachmarkInteraction = () => {
    setShowCoachmark(false);
  };

  if (!showCoachmark) {
    return null;
  }

  return (
    <CoachmarkWrapper>
      <Coachmark
        id={coachmarkConfig.id}
        title={activeConfig.title}
        description={activeConfig.description}
        imageURL={activeConfig.iconUrl}
        cta={activeConfig.cta}
        onInteraction={handleCoachmarkInteraction}
        onDismiss={handleCoachmarkDismiss}
        iconWidth={32}
        iconHeight={32}
        pointerClassName="store-toggle-coachmark-pointer-desktop"
      />
    </CoachmarkWrapper>
  );
}

export default StoreToggleCoachmark;
